import React, { useCallback, useEffect, useState } from "react";

// region Imports - Graphql
import { DocumentNode, useLazyQuery, useQuery } from "@apollo/client";
import {
  concreteStrengthCertificateAllWithSortedPagination,
  TConcreteStrengthCertificateAllWithSortedPaginationQueries
} from "@graphql/groups/concreteStrengthCertificate/query.graphql";
import {
  ConcreteStrengthCertificateQueriesGetAllWithSortedPaginationArgs,
  ConcreteStrengthCertificateType,
  ECommandResultStatus,
  ESort,
  EWhereClausesOperators,
  PermissionUserQueriesGetAllWithoutPaginationArgs
} from "@store/graphql/helpers/types.graphql";
import {
  permissionUserAllWithoutPagination,
  TPermissionAllWithoutPaginationQueries
} from "@graphql/groups/permissionUser/query.graphql";
// endregion Imports - Graphql
// region Imports - Telluria Utils
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
// endregion Imports - Telluria Utils
// region Imports - Languages
import { AlertMessages, CustomerMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
// endregion Imports - Languages
// region Imports - Hooks
import { useAuth } from "@hooks/useAuth";
// endregion Imports - Hooks
// region Imports - Stores
import Store from "@store/jotai";
import { ModulesID } from "@store/constants/index";
// endregion Imports - Stores
// region Imports - Libraries
import { Grid } from "@libraries/mui";
// endregion Imports - Libraries
// region Imports - Helpers
import utils from "@helpers/utils";
// endregion Imports - Helpers
// region Imports - Atoms
import AlertContentByClient from "@atoms/AlertContentByClient";
import AlertDialog from "@atoms/AlertDialog";
import { Loading } from "@atoms/index";
import ShowMoreButton from "@atoms/ShowMoreButton";
import { useSelectClient } from "@atoms/SelectClient";
// endregion Imports - Atoms
// region Imports - Molecules
import { BaseCard, BaseCardAction, BaseCardHeader, PageHeader } from "@molecules/index";
// endregion Imports - Molecules
// region Imports - Pages
import Office from "../Office";
// endregion Imports - Pages
// region Imports - Styles
import * as Styled from "./styles";
// endregion Imports - Styles

// region Interfaces
interface WhereCertificateIdsByClient {
  clientId: string;
  certificateIds: string;
}
// endregion Interfaces
const Certificate: React.FC = () => {

  const { t } = useTranslation();
  const {
    value: concreteStrengthCertificates,
    setValue: setConcreteStrengthCertificates,
    filteredCertificates,
    setFilteredCertificates
  } = Store.useCertificates();

  // region Hooks
  const { value: selectedClient } = useSelectClient();
  const { addToast } = useToast();
  const { getDecodedToken } = useAuth();
  // endregion Hooks
  // region States
  const [nextPage, setNextPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [whereCertificateIdsByClient, setWhereCertificateIdsByClient] = useState<
    WhereCertificateIdsByClient[]>([]);

  const [alertDialogOpen, setAlertDialogOpen] = useState<boolean>(false);
  const [alertDialogMessage, setAlertDialogMessage] = useState<string>("");
  const [isPermissionDialogAlert, setIsPermissionDialogAlert] = useState<boolean>(false);

  const [noPermissionInCurrentClient, setNoPermissionInCurrentClient] = useState<boolean>(false);
  const [noDataInCurrentClient, setNoDataInCurrentClient] = useState<boolean>(false);
  // endregion States
  // region Constants
  const token = getDecodedToken();

  const limitPerPage = 12;
  // endregion Constants
  // region GraphQL - Queries
  const {
    loading: loadingPermissions,
    error: errorPermissions,
    data: dataPermissions
  } = useQuery<
    TPermissionAllWithoutPaginationQueries,
    PermissionUserQueriesGetAllWithoutPaginationArgs
  >(
    permissionUserAllWithoutPagination([
      "contract.concretings.concreteStrengthCertificates.id", "construction.client.id"
    ]) as DocumentNode,
    {
      fetchPolicy: "network-only",
      pollInterval: 60000,
      variables: {
        where: [
          {
            property: "user.id",
            operator: EWhereClausesOperators.EQUALS,
            value: token.primarysid
          },
          {
            property: "module.id",
            operator: EWhereClausesOperators.EQUALS,
            value: ModulesID.CERTIFICATE
          }
        ]
      }
    }
  );

  const [
    getCertificates,
    {
      loading: loadingCertificates,
      error: errorCertificates,
      data: dataCertificates
    }
  ] = useLazyQuery<
    TConcreteStrengthCertificateAllWithSortedPaginationQueries,
    ConcreteStrengthCertificateQueriesGetAllWithSortedPaginationArgs
  >(
    concreteStrengthCertificateAllWithSortedPagination([
      "id",
      "issueDate",
      "archive.id",
      "archive.description",
      "archive.originalName",
      "archive.extension",
      "archive.storageUrl",
      "concreting.scheduledDate",
      "concreting.contract.code",
      "concreting.contract.construction.name"
    ]),
    {
      fetchPolicy: "network-only",
      pollInterval: 60000
    }
  );
  // endregion GraphQL - Queries

  // region Functions
  /**
   * Render the actions button of the certificate card
   */
  const actionCertificate = useCallback(
    (certificate: ConcreteStrengthCertificateType) => {
      if (!certificate.archive) return undefined;

      return (
        <BaseCardAction type="certificate" archive={certificate.archive} />
      );
    },
    []
  );

  const handleNextPage = () => setNextPage(nextPage + 1);

  /**
   * Load the certificates
   */
  const loadCertificates = useCallback(async () => {

    const response = dataCertificates?.concreteStrengthCertificate.getAllWithSortedPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      setTotalPages(response.pageCount);
      const certificatesList = response.result;

      if (nextPage > 1) {
        setConcreteStrengthCertificates(
          concreteStrengthCertificates.concat(
            certificatesList as ConcreteStrengthCertificateType[]
          )
        );
      } else {
        setConcreteStrengthCertificates(
          certificatesList as ConcreteStrengthCertificateType[]
        );
      }
    }
  }, [dataCertificates, setConcreteStrengthCertificates]);

  /**
   * Make the where clause to get the specified certificates according to user permissions
   */
  const makeWhereCertificateIds = useCallback(() => {
    const response = dataPermissions?.permissionUser.getAllWithoutPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      if (response.result?.length === 0) {
        setWhereCertificateIdsByClient([]);

        setIsPermissionDialogAlert(true);
        setAlertDialogMessage(t(AlertMessages.noPermissionCertificates));
        setAlertDialogOpen(true);

        return;
      }

      const whereCertificateIdsByClients: WhereCertificateIdsByClient[] = [];

      response.result?.forEach((permission) => {
        if (permission?.construction?.client?.id) {

          let concatenatedCertificateIds = "";

          permission?.contract?.concretings?.forEach((concreting) => {

            concreting?.concreteStrengthCertificates?.forEach((certificate) => {
              if (certificate?.id && certificate.id !== "") {
                concatenatedCertificateIds = utils.concatWithSeparator(concatenatedCertificateIds, certificate.id, "|");
              }
            });

          });

          const whereInvoiceIdsByClient = whereCertificateIdsByClients.find(
            (whereInvoiceIdsByClient) => whereInvoiceIdsByClient.clientId === permission.construction?.client?.id
          );

          if (whereInvoiceIdsByClient) {
            whereInvoiceIdsByClient.certificateIds = utils.concatWithSeparator(
              whereInvoiceIdsByClient.certificateIds,
              concatenatedCertificateIds,
              "|"
            );
          } else {
            whereCertificateIdsByClients.push({
              clientId: permission.construction.client.id,
              certificateIds: concatenatedCertificateIds
            });
          }
        }
      });

      setWhereCertificateIdsByClient(whereCertificateIdsByClients);

      const certificateIds = whereCertificateIdsByClients.find(
        (certificatesByClient) => certificatesByClient.certificateIds !== ""
      );

      if (!certificateIds) {
        setIsPermissionDialogAlert(false);
        setAlertDialogMessage(t(AlertMessages.noHaveCertificates));
        setAlertDialogOpen(true);
      }
    }
  }, [dataPermissions, setFilteredCertificates]);

  // endregion Functions
  // region UseEffects
  /**
   * Reset the certificates when the selected client changes
   */
  useEffect(() => {
    setConcreteStrengthCertificates([]);
    setNextPage(1);
    setTotalPages(0);
    setNoDataInCurrentClient(false);
    setNoPermissionInCurrentClient(false);
  }, [selectedClient, setConcreteStrengthCertificates]);

  /**
   * Execute certificate query when selected client changes
   */
  useEffect(() => {
    const whereCertificateByClient = whereCertificateIdsByClient.find(
      (certificateByClient) => certificateByClient.clientId === selectedClient?.id
    );

    if (!whereCertificateByClient) {
      setNoPermissionInCurrentClient(true);

      return;
    }

    setNoPermissionInCurrentClient(false);

    const whereCertificateIds = whereCertificateByClient?.certificateIds;

    if (whereCertificateIds === "") {
      setNoDataInCurrentClient(true);

      return;
    }

    setNoDataInCurrentClient(false);

    getCertificates({
      variables: {
        page: nextPage,
        perPage: limitPerPage,
        sort: [
          {
            field: "issueDate",
            sortDirection: ESort.DESC
          }
        ],
        where: [
          {
            property: "id",
            operator: EWhereClausesOperators.IN,
            value: whereCertificateIds
          }
        ]
      }
    });
  }, [getCertificates, whereCertificateIdsByClient, nextPage, selectedClient]);

  /**
   * Process certificate permissions to get invoices ids by client
   */
  useEffect(() => {
    if (dataPermissions) makeWhereCertificateIds();
    if (errorPermissions) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorPermissions.message
      });
    }
  }, [dataPermissions, errorPermissions, addToast, makeWhereCertificateIds]);

  /**
   * Process certificates query response when invoices query changes
   */
  useEffect(() => {
    if (dataCertificates) loadCertificates();
    if (errorCertificates) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorCertificates.message
      });
    }
  }, [addToast, dataCertificates, errorCertificates, loadCertificates]);

  /**
   * Set the filtered certificates
   */
  useEffect(() => {
    setFilteredCertificates(concreteStrengthCertificates);
  }, [concreteStrengthCertificates, setFilteredCertificates]);
  // endregion UseEffects

  return (
    <Office>
      <PageHeader title={t(CustomerMessages.applicationCertificate)} url="/" />

      <Styled.Container>
        <AlertDialog
          open={alertDialogOpen}
          title={alertDialogMessage}
          onClose={() => setAlertDialogOpen(false)}
          iconColor={isPermissionDialogAlert ? "red" : "yellow"}
        />
        <Grid container spacing={2}>
          {(loadingCertificates || loadingPermissions) && (
            <Grid item xs={12}>
              <Loading loading />
            </Grid>
          )}
          {!loadingPermissions
            && !loadingCertificates
            && filteredCertificates
            && filteredCertificates.map((certificate) => {
              const cardHeader = (
                <BaseCardHeader type="certificate" data={certificate} />
              );

              return (
                <Grid key={`grid-${certificate.id}`} item xs={12} sm={6} md={4}>
                  <BaseCard
                    key={certificate.id}
                    id={certificate.id}
                    header={cardHeader}
                    actions={actionCertificate(certificate)}
                  />
                </Grid>
              );
            })}
          {!loadingPermissions && !loadingCertificates && (noPermissionInCurrentClient || noDataInCurrentClient) && (
            <AlertContentByClient
              alertMessage={noPermissionInCurrentClient
                ? t(AlertMessages.noPermissionCertificatesSpecificClient)
                : t(AlertMessages.noHaveCertificatesSpecificClient)}
              iconColor={noPermissionInCurrentClient ? "red" : "yellow"}
            />
          )}
        </Grid>
        {filteredCertificates
          && nextPage < totalPages
          && (
            <Styled.ContainerShowMoreButton>
              <ShowMoreButton onClick={handleNextPage} />
            </Styled.ContainerShowMoreButton>
          )}
      </Styled.Container>
    </Office>
  );
};

export default Certificate;
